import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivateAccount } from '@app/core/models';
import { AccountService } from '@app/core/services/account.service';
import { ConfigService } from '@app/core/services/config.service';
import { NotificationsService } from '@app/core/services/notifications.service';
import { PasswordBaseComponent } from '@app/shared/components/forms/password-base.component';
import { NotificationTypes } from '@core/models/notification/notification-types';

declare global {
  interface Window {
    AwsWafCaptcha: any;
  }
}

@Component({
  standalone: false,
  templateUrl: './activate-account.component.html'
})
export class ActivateAccountComponent extends PasswordBaseComponent implements OnInit, AfterViewInit {

  isProcessing = false;
  id = '';
  passwordRequired = false;
  isActiveUser = false;
  formSetupComplete = false;

  siteKey: string;
  captchaToken: string | null = null;
  captchaContainer: any;
  private captchaScriptLoaded = false;
  private captchaApiKey: string | null = null;

  public recentError?: { error: any };

  @ViewChild('submitB') submitB: ElementRef<HTMLInputElement>;
  @ViewChild('captchaContainer', { static: false }) captchaElement: ElementRef;

  constructor(private readonly activatedRoute: ActivatedRoute,
    private readonly notificationsService: NotificationsService,
    private readonly accountService: AccountService,
    private readonly router: Router,
    protected formBuilder: FormBuilder, private readonly configService: ConfigService) {

    super(formBuilder);

  }

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.queryParams.id;

    this.accountService
      .isPasswordRequired(this.id)
      .then((response) => {
        this.passwordRequired = response.passwordRequired;
        this.isActiveUser = response.activeUser;
      })
      .finally(() => {
        this.isProcessing = false;
        if (this.isActiveUser) {
          this.router.navigate(['/']).then(() =>
            this.notificationsService.notify({
              target: 'login',
              text: 'Your account is already active. Please enter your login credentials.',
              headline: '',
              type: NotificationTypes.SUCCESS,
              dismissible: true,
              dismissAfter: 3000
            })
          );
        }
        // This code is required to bypass the validation when passwordRequired=false
        if (!this.passwordRequired) {
          this.form.get('password').disable();
          this.form.get('password').updateValueAndValidity();
          this.form.get('confirmPassword').disable();
          this.form.get('confirmPassword').updateValueAndValidity();
        }
        // Mark form setup as complete
        this.formSetupComplete = true;
        // Load script and initialize captcha if view is already ready
        if (this.captchaElement) {
          this.fetchCaptchaApiKey().then(() => {
            this.loadCaptchaScript().then(() => {
              this.initializeCaptcha();
            });
          }).catch(error => {
            console.error('Failed to fetch captcha API key:', error);
            this.notificationsService.notify({
              target: 'activate',
              text: 'Error loading security verification. Please refresh the page.',
              headline: '',
              type: NotificationTypes.DANGER,
              dismissible: true,
              dismissAfter: 5000
            });
          });
        }
      });

    super.buildForm();
  }

  ngAfterViewInit(): void {
    // Load script and initialize captcha when view is ready and form setup is complete
    if (this.formSetupComplete && !this.isActiveUser) {
      this.fetchCaptchaApiKey().then(() => {
        this.loadCaptchaScript().then(() => {
          this.initializeCaptcha();
        });
      }).catch(error => {
        console.error('Failed to fetch captcha API key:', error);
        this.notificationsService.notify({
          target: 'activate',
          text: 'Error loading security verification. Please refresh the page.',
          headline: '',
          type: NotificationTypes.DANGER,
          dismissible: true,
          dismissAfter: 5000
        });
      });
    }
  }

  fetchCaptchaApiKey(): Promise<void> {
    return this.accountService.getCaptchaApiKey()
      .then(response => {
        this.captchaApiKey = response.apiKey;
      });
  }

  loadCaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (this.captchaScriptLoaded || window.AwsWafCaptcha) {
        this.captchaScriptLoaded = true;
        resolve();
        return;
      }

      // Check if script element already exists
      const existingScript = document.querySelector('script[src*="jsapi.js"]');
      if (existingScript) {
        // Wait for existing script to load
        existingScript.addEventListener('load', () => {
          this.captchaScriptLoaded = true;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          reject(new Error('Failed to load captcha script'));
        });
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.configService.appConfig.awsCaptcha.scriptUrl;
      script.async = true;

      script.onload = () => {
        this.captchaScriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load captcha script'));
      };

      document.head.appendChild(script);
    });
  }

  initializeCaptcha(): void {
    // Wait for the AWS WAF CAPTCHA script to load
    const checkCaptchaLoaded = setInterval(() => {
      if (window.AwsWafCaptcha && this.captchaElement) {
        clearInterval(checkCaptchaLoaded);
        this.renderCaptcha();
      }
    }, 100);

    // Clear interval after 10 seconds if CAPTCHA doesn't load
    setTimeout(() => clearInterval(checkCaptchaLoaded), 10000);
  }

  renderCaptcha(): void {
    if (!this.captchaApiKey) {
      console.error('CAPTCHA API key not available');
      return;
    }

    try {
      this.captchaContainer = window.AwsWafCaptcha.renderCaptcha(
        this.captchaElement.nativeElement,
        {
          onSuccess: (token: string) => {
            this.captchaToken = token;
            console.log('CAPTCHA token received');
            // Hide captcha container after successful verification
            if (this.captchaElement?.nativeElement) {
              this.captchaElement.nativeElement.style.display = 'none';
            }
          },
          onError: (error: any) => {
            console.error('CAPTCHA error:', error);
            this.notificationsService.notify({
              target: 'activate',
              text: 'Error loading security verification. Please refresh the page.',
              headline: '',
              type: NotificationTypes.DANGER,
              dismissible: true,
              dismissAfter: 5000
            });
          },
          dynamicWidth: true,
          skipTitle: false,
          apiKey: this.captchaApiKey
        }
      );
    } catch (error) {
      console.error('Failed to render CAPTCHA:', error);
    }
  }

  onSubmit(): void {
    // Validate CAPTCHA token before submitting
    if (!this.captchaToken) {
      this.notificationsService.notify({
        target: 'activate',
        text: 'Please complete the security verification before submitting.',
        headline: '',
        type: NotificationTypes.WARNING,
        dismissible: true,
        dismissAfter: 5000
      });
      return;
    }

    const activateAccountData = {
      newPassword: this.form.value.password,
      token: this.form.value.pin,
      id: this.id,
      captchaToken: this.captchaToken // Include CAPTCHA token in request
    } as ActivateAccount;

    this.isProcessing = true;
    this.notificationsService.clearAll();

    this.accountService
      .activateAccount(activateAccountData)
      .then(() => {
        this.router.navigate(['/']).then(() =>
          this.notificationsService.notify({
            target: 'login',
            text: 'Your account has been successfully activated. If you have access to ' +
              'Labcorp | Link&trade;, the same login credentials can be used for both systems.',
            headline: '',
            type: NotificationTypes.SUCCESS,
            dismissible: true,
            dismissAfter: 3000
          })
        );
      })
      .catch((err: HttpErrorResponse) => {
        if (err.statusText === 'INVALID_REQUEST') {
          this.notificationsService.notify({
            target: 'activate',
            text: 'We were not able to activate your account at this time.',
            headline: '',
            type: NotificationTypes.DANGER,
            dismissible: false,
            dismissAfter: 10000
          });
        } else if (err.statusText === 'TOKEN_EXPIRED') {
          this.notificationsService.notify({
            target: 'activate',
            text: 'Your PIN has expired, please contact your customer administrator or Managed Care Executive to re-send your activation.',
            headline: '',
            type: NotificationTypes.DANGER,
            dismissible: false,
            dismissAfter: 10000
          });
        } else if (err.status === 405) {
          // CAPTCHA validation failed
          this.notificationsService.notify({
            target: 'activate',
            text: 'Security verification failed. Please try again.',
            headline: '',
            type: NotificationTypes.DANGER,
            dismissible: true,
            dismissAfter: 5000
          });
          // Reset CAPTCHA token and re-render
          this.captchaToken = null;
          if (this.captchaElement) {
            this.captchaElement.nativeElement.innerHTML = '';
            this.renderCaptcha();
          }
        }
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }
}
